import { useTable } from "@refinedev/core";
import { Link } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Table, Td, Th, Tr } from "@/components/Table";
import { formatDateTime } from "@/lib/format";
import { filterValue } from "@/lib/query";

export interface UserRow extends Record<string, unknown> {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  email_verified_at: string | null;
  last_login_at: string | null;
  created_at: string;
  families_count: number;
}

export function UsersListPage() {
  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<UserRow>({
      resource: "users",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Konta rodzin"
        description="Wyszukiwarka kont i ich rodzin. Blokada konta zamyka dostęp, ale niczego nie kasuje - historie zostają na miejscu."
      />

      <FilterBar>
        <SearchInput
          label="Szukaj"
          placeholder="adres e-mail albo imię"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
        <Select
          label="Stan konta"
          value={filterValue(filters, "is_active")}
          options={[
            { value: "true", label: "Czynne" },
            { value: "false", label: "Zablokowane" },
          ]}
          onChange={(value) => ustawFiltr("is_active", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Osoba</Th>
                <Th>E-mail</Th>
                <Th align="right">Rodziny</Th>
                <Th>Ostatnie logowanie</Th>
                <Th>Konto założone</Th>
                <Th>Stan</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Żadne konto nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((user) => (
                <Tr key={user.id}>
                  <Td nowrap>
                    <Link
                      to={`/uzytkownicy/${user.id}`}
                      className="text-accent underline underline-offset-4 hover:text-accent-dark"
                    >
                      {user.name}
                    </Link>
                  </Td>
                  <Td>{user.email}</Td>
                  <Td align="right" numeric>
                    {user.families_count}
                  </Td>
                  <Td nowrap>{formatDateTime(user.last_login_at)}</Td>
                  <Td nowrap>{formatDateTime(user.created_at)}</Td>
                  <Td>
                    <Badge tone={user.is_active ? "ok" : "error"}>
                      {user.is_active ? "Czynne" : "Zablokowane"}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
